<template>
  <ak-sidebar>
    <ak-loading
      v-model:active="isLoading"
      :can-cancel="false"
      :is-full-page="false"
      :backdrop="false"
    />
    <div class="flex flex--column h-full">
      <ak-box size="small" class="flex">
        <h4 class="color-grey mb-none">Projects</h4>
        <create-dropdown
          @create-folder="openManageFolderModal"
          @create-project="openManageCampaignModal"
        />
      </ak-box>
      <ak-tabs
        v-model="activeTab"
        justified
        narrow-nav
        class="flex flex--column h-full"
        style="overflow: hidden"
      >
        <ak-tab-item label="Folders">
          <folders-tab v-if="!isLoading" v-model:tree="tree" :list="treeList">
            <template #searchFilter>
              <search-filter
                v-model:search="searchFilter"
                v-model:filter="filter"
                show-filter
                @change="updateData"
              />
            </template>
          </folders-tab>
        </ak-tab-item>
        <ak-tab-item label="List">
          <list-tab v-if="!isLoading" :list="list">
            <template #searchFilter>
              <search-filter
                v-model:search="searchFilter"
                v-model:filter="filter"
                v-model:sort="sortOrder"
                show-sort
                show-filter
                @change="updateData"
              />
            </template>
          </list-tab>
        </ak-tab-item>
        <ak-tab-item v-if="isOwnAccount" label="Favorites">
          <favorites-tab v-if="!isLoading" :favorites="favorites">
            <template #searchFilter>
              <search-filter
                v-model:search="searchFilter"
                v-model:filter="filter"
                v-model:sort="sortOrder"
                show-sort
                show-filter
                @change="updateData"
              />
            </template>
          </favorites-tab>
        </ak-tab-item>
      </ak-tabs>
    </div>
  </ak-sidebar>
</template>

<script>
import {
  defineComponent,
  computed,
  ref,
  onMounted,
  watch,
  provide,
  nextTick,
} from 'vue'
import Sidebar from '@akerolabs/akero-vue/src/components/sidebar/Sidebar.vue'
import Loading from '@akerolabs/akero-vue/src/components/loading/Loading.vue'
import Box from '@akerolabs/akero-vue/src/components/box/Box.vue'
import Tabs from '@akerolabs/akero-vue/src/components/tabs/Tabs.vue'
import TabItem from '@akerolabs/akero-vue/src/components/tabs/TabItem.vue'
import CreateDropdown from './dropdowns/CreateDropdown.vue'
import SearchFilter from './SearchFilter.vue'
import FoldersTab from './tabs/FoldersTab.vue'
import ListTab from './tabs/ListTab.vue'
import FavoritesTab from './tabs/FavoritesTab.vue'
import { ModalProgrammatic } from '@akerolabs/akero-vue/src/components/modal'
import CreateProjectModal from '../../projects/CreateProjectModal.vue'
import EditProjectModal from '../../projects/EditProjectModal.vue'
import { request } from '@akerolabs/akero-vue/src/utils/request'
import { error } from './utils'
export default defineComponent({
  name: 'ProjectSidebar',
  components: {
    [Sidebar.name]: Sidebar,
    [Loading.name]: Loading,
    [Box.name]: Box,
    [CreateDropdown.name]: CreateDropdown,
    [Tabs.name]: Tabs,
    [TabItem.name]: TabItem,
    [SearchFilter.name]: SearchFilter,
    [FoldersTab.name]: FoldersTab,
    [ListTab.name]: ListTab,
    [FavoritesTab.name]: FavoritesTab,
    [CreateProjectModal.name]: CreateProjectModal,
    [EditProjectModal.name]: EditProjectModal,
  },
  props: {
    /**
     * @type {Object}
     * @description The active account (drilled in or users)
     */
    accountId: {
      default: undefined,
      type: String,
    },
    /**
     * @type {Object}
     * @description The active agency (drilled in or users)
     */
    agencyId: {
      default: undefined,
      type: String,
    },
    /**
     * @type {Object}
     * @description The users profile data
     */
    userProfile: {
      default: () => ({}),
      type: Object,
    },
    activeFilter: {
      type: Array,
      default: () => [],
    },
  },
  emits: ['update:activeFilter'],
  setup(props, { emit }) {
    const isLoading = ref(false)

    const filterModel = ref(props.activeFilter)
    const activeFilter = ref(null)
    const allFilter = ref([])

    const activeTab = ref(0)
    const tree = ref([])
    const treeList = ref([])
    const list = ref([])
    const favorites = ref([])
    const searchFilter = ref('')
    const openFolders = ref([])
    const filter = ref({
      archived: true,
      agencyActivity: true,
    })
    const user = ref({})
    const userFavorites = ref([])
    const sortOrder = ref('desc')

    watch(activeTab, async () => {
      await updateData()
      nextTick(() => {
        setProjectFilter(null)
      })
    })

    watch(sortOrder, (order) => {
      const sortFnc = order === 'desc' ? sortByCreatedOn : sortByTitle
      if (activeTab.value === 1) {
        list.value = sortFnc(list.value)
      }
      if (activeTab.value === 2) {
        favorites.value = sortFnc(favorites.value)
      }
    })

    watch(
      () => props.activeFilter,
      (value) => {
        filterModel.value = value
      },
      { deep: true },
    )

    watch(
      filterModel,
      (value) => {
        emit('update:activeFilter', value)
      },
      { deep: true },
    )

    const openManageFolderModal = (parent = null, edit = null) => {
      const props = {
        modelValue: {},
        isFolder: true,
      }
      if (parent) props.parentId = parent
      if (edit) props.modelValue = { ...edit }
      const component = edit ? EditProjectModal : CreateProjectModal
      ModalProgrammatic.open({
        component,
        outerScroll: true,
        submitForm: submitCallback,
        size: 'large',
        props,
      })
    }

    const submitCallback = async () => {
      await updateData()
    }

    const openManageCampaignModal = (parent = null, edit = null) => {
      const props = {
        modelValue: {},
        isFolder: false,
      }
      if (parent) props.parent = parent
      if (edit) props.modelValue = { ...edit }
      const component = edit ? EditProjectModal : CreateProjectModal
      ModalProgrammatic.open({
        component,
        outerScroll: true,
        submitForm: submitCallback,
        size: 'large',
        props,
      })
    }

    const sortByTitle = (list) => {
      return list.sort((a, b) => {
        return a.title > b.title
      })
    }

    const sortByCreatedOn = (list) => {
      return list.sort((a, b) => {
        return new Date(a.createdOn) > new Date(b.createdOn)
      })
    }

    /**
     * @description Generic api query function
     * @param {Object} query
     * @return {Array}
     */
    const fetchData = async (query) => {
      const { data } = await request.get('/campaigns/tree', {
        params: {
          ...query,
          ...filterRequest.value,
          account: props.accountId,
          agency: props.agencyId,
          per_page: 0,
        },
      })
      allFilter.value = data.data.map((i) => {
        return i._id
      })
      return data.data
    }

    /**
     * @description Fetch folders for the folders tab view
     */
    const fetchCampaignFolders = async () => {
      const data = await fetchData({})
      treeList.value = data
      let temp = []
      if (filterRequest.value.isFolder === false) {
        // running a search so there will be no children and we don't need to consider the parent
        data.forEach((i) => {
          temp.push({ data: i, children: [] })
        })
      } else {
        // call recursive function to sort the data into folders
        temp = getChildren(data, null)
      }
      const folders = temp.filter((i) => {
        return i.data.isFolder
      })
      const projects = temp.filter((i) => {
        return !i.data.isFolder
      })
      tree.value = [...folders, ...projects]
    }

    provide('updateTree', () => {
      tree.value = [...tree.value]
    })

    /**
     * @description Recursive function to build the tree array
     * @param {Array} data
     * @param {String} parent
     * @return {Array}
     */
    const getChildren = (data, parent) => {
      const children = data
        .filter((i) => {
          // check the parent
          return i.parent === parent
        })
        .sort((a, b) => {
          // sort by position
          return a.position > b.position
        })
        .map((i) => {
          // map the data into tree ready objects
          return { data: i, children: [] }
        })
      // loop though the children
      for (let i = 0; i < children.length; i++) {
        const id = children[i].data._id
        // check for any children
        const hasChild = data.find((item) => {
          return item.parent === id
        })
        if (hasChild) {
          // if we have at least one child recursively call the function to populate the next level down
          children[i].children = getChildren(data, id)
        }
      }
      return children
    }

    /**
     * @description Fetch campaigns for the list tab view
     */
    const fetchCampaignList = async () => {
      const data = await fetchData({
        isFolder: JSON.stringify({ $ne: true }),
        sort: '-createdOn',
      })
      list.value = data
    }

    const fetchFavoritesList = async () => {
      const data = await fetchData({
        _id: JSON.stringify({ $in: userFavorites.value }),
        isFolder: JSON.stringify({ $ne: true }),
        sort: '-createdOn',
      })
      favorites.value = data
    }

    const fetchUser = async () => {
      const { data } = await request.get(`/users/${props.userProfile._id}`)
      user.value = data.data
      if (data.data && data.data.accounts) {
        const account = data.data.accounts.find((i) => {
          return i.account === props.accountId
        })
        if (account) {
          userFavorites.value = account.favoriteCampaigns
        }
      }
    }

    const folderOpened = ({ _id }) => {
      if (!openFolders.value.includes(_id)) {
        openFolders.value.push(_id)
      }
    }

    const folderClosed = ({ _id }) => {
      openFolders.value = openFolders.value.filter((i) => {
        return i != _id
      })
    }

    const isFolderOpen = ({ _id }) => {
      return openFolders.value.includes(_id)
    }

    /**
     * @description Update the data for each view type
     */
    const updateData = async () => {
      tree.value = []
      treeList.value = []
      list.value = []
      favorites.value = []
      isLoading.value = true
      try {
        await fetchUser()
        switch (activeTab.value) {
          case 0:
            await fetchCampaignFolders()
            break
          case 1:
            await fetchCampaignList()
            break
          case 2:
            await fetchFavoritesList()
            break
          default:
            break
        }
      } catch (ex) {
        error('Failed to fetch data', ex)
      }
      isLoading.value = false
    }

    /**
     * @description Build the request data for the filter values
     * @type {Object}
     */
    const filterRequest = computed(() => {
      const filterData = {}
      if (searchFilter.value && searchFilter.value.length > 0) {
        filterData.title = searchFilter.value
        filterData.regex = true
        filterData.isFolder = false
        filterData.blacklist = '_id'
      }
      if (!filter.value.archived) {
        filterData.archived = JSON.stringify({ $ne: true })
      }
      if (!filter.value.agencyActivity) {
        filterData.agencyActivity = JSON.stringify({ $ne: true })
      }
      return filterData
    })

    const setProjectFilter = (id) => {
      if (id === null) {
        filterModel.value = allFilter.value
        activeFilter.value = null
      } else {
        filterModel.value = [id]
        activeFilter.value = id
      }
    }

    const setFolderFilter = (id, children) => {
      filterModel.value = [id, ...children]
      activeFilter.value = id
    }

    const isOwnAccount = computed(() => {
      const foundAccount = props.userProfile.accounts.find(
        ({ account }) => account._id === props.accountId,
      )
      if (foundAccount) return true
      return false
    })

    provide('accountId', props.accountId)
    provide('agencyId', props.agencyId)
    provide('setProjectFilter', setProjectFilter)
    provide('setFolderFilter', setFolderFilter)
    provide('updateData', updateData)
    provide('folderOpened', folderOpened)
    provide('folderClosed', folderClosed)
    provide('isFolderOpen', isFolderOpen)
    provide('openManageFolderModal', openManageFolderModal)
    provide('openManageCampaignModal', openManageCampaignModal)
    provide('isFavorite', (id) => {
      return userFavorites.value.includes(id)
    })
    provide('isActive', (id) => {
      if (!id && !activeFilter.value) return true
      return id === activeFilter.value
    })
    provide('isOwnAccount', () => {
      return isOwnAccount.value
    })

    onMounted(async () => {
      await updateData()
      setProjectFilter(null)
    })

    return {
      isLoading,
      searchFilter,
      filter,
      sortOrder,
      openManageFolderModal,
      openManageCampaignModal,
      activeTab,
      tree,
      treeList,
      list,
      favorites,
      updateData,
      isOwnAccount,
    }
  },
})
</script>

<style lang="scss">
@import '@akerolabs/akero-vue/src/scss/_settings.scss';
.campaign-list-item {
  border-radius: 5px;
  padding: 0.375rem;
  display: block;
  transition: 0.2s ease;
  cursor: pointer;
  &:hover {
    background-color: $color-grey-lighter;
  }
}

.is-archived {
  background-color: rgba(226, 147, 29, 0.2);
  &:hover {
    background-color: rgba(226, 147, 29, 0.2);
  }
}
.is-active-project {
  color: $color-grey;
  font-weight: 700;
}
</style>
